import { useNavigate } from "react-router-dom";
import { useState, useCallback, useEffect } from "react";
import { useGridApiRef, gridPaginationModelSelector } from "@mui/x-data-grid-pro";
// components
import { DataGridPremium } from "@mui/x-data-grid-premium";
import SchoolIcon from "@mui/icons-material/School";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import PlaceIcon from "@mui/icons-material/Place";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import GroupIcon from "@mui/icons-material/Group";
import { formatDate } from "../../utils/dateTimeFormat";
import { MemToolBar, dataGridLang } from "../MemToolBar";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import DotMenu from "../../components/DotMenu";
import Translate from "../../utils/Translate";
import CourseDialog from "./CourseDialog";
import PublishToWeb from "./PublishToWeb";
import { CustomNoRowsOverlay } from "../../utils/StyledGridOverlay";
import { IconAndLabelColumnHeader, TableAvatar2BorderRadius } from "../../utils/dataGrid";
import PublicIcon from "@mui/icons-material/Public";
import { hasAccess, accessKeys } from "../../utils/userAccess";
import { useMediaQuery, useTheme } from "@mui/material";
import { ToolBar } from "../ToolBar";

const getCourseDotMenu = (
  courseId,
  numberOfBookings,
  publishFromDate,
  publishToDate,
  autoUnpublishWhenFull,
  fromDate,
  onDeleteCourse,
) => {
  const menudata = [
    {
      icon: <EditIcon />,
      text: Translate.get("EditCourse"),
      route: false,
      dialogHeader: Translate.get("EditCourse"),
      component: <CourseDialog courseId={courseId} />,
    },
    {
      icon: <PublicIcon />,
      text: !!publishFromDate ? Translate.get("EditIframePublish") : Translate.get("ePort2iFramePublishOnline"),
      route: false,
      customClass: "smallCourseDialog",
      component: (
        <PublishToWeb
          courseId={courseId}
          publishFromDate={publishFromDate}
          publishToDate={publishToDate}
          fromDate={fromDate}
          autoUnpublishWhenFull={autoUnpublishWhenFull}
        />
      ),
      hide: !hasAccess(accessKeys.iframe),
    },
    {
      icon: <DeleteIcon />,
      text: Translate.get("RemoveCourse"),
      route: false,
      dialogHeader: Translate.get("RemoveCourse"),
      onClick: () => onDeleteCourse(courseId),
      disabled: numberOfBookings > 0,
    },
  ];
  return menudata;
};

const parseData = (data) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today.getFullYear(), now.getMonth(), now.getDate() + 1);

  let dateSpan = "Pågående";
  return data.map((row) => {
    const parsedFrom = new Date(row.fromDate);
    const parsedTo = new Date(row.toDate);

    if (parsedTo < today) {
      dateSpan = "Avslutad";
    } else if (parsedFrom < tomorrow) {
      dateSpan = "Pågående";
    } else {
      dateSpan = "Planerad";
    }
    row.dateSpan = dateSpan;
    return row;
  });
};

const Comp = ({
  rows,
  smallScreen,
  onDeleteCourse,
  hideFilter = false,
  etitMenu = true,
  autoSize = false,
  hideDotMenu,
  loadFilters,
}) => {
  const localizedTextsMap = dataGridLang();
  rows = parseData(rows);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 15,
    page: 0,
  });
  const [filterModel, setFilterModel] = useState({ items: [] });

  let sizeProps = {
    autoHeight: true,
    paginationModel: paginationModel,
  };
  if (autoSize) {
    sizeProps = {
      autoPageSize: true,
    };
  }

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const navigate = useNavigate();

  useEffect(() => {
    if (localStorage.getItem("savedFilters") !== null && loadFilters === true) {
      let js = localStorage.getItem("savedFilters");
      let f = JSON.parse(js);
      setFilterModel(f);
    }
    getPagination(apiRef, "courses");
  }, []);

  let columns = [];
  let toolbarCss = "stdVisible dgDynIconText";
  if (hideFilter) {
    toolbarCss = "dgNoFilter dgNoIconText";
  }
  if (!isSmallScreen) {
    columns = [
      {
        field: "name",
        headerName: Translate.get("CourseType"),
        headerClassName: "hideSeparator",
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={SchoolIcon} label={Translate.get("CourseType")} />,
        renderCell: (params) => {
          return (
            <div style={{ marginTop: "1em", minWidth: "300px" }}>
              <TableAvatar2BorderRadius
                image={params.row.imageUrl}
                title={params.row.name}
                subtitle={params.row.shortComment}
              />
            </div>
          );
        },
      },
      {
        field: "dateSpan",
        headerName: Translate.get("Status"),
        headerClassName: "hideSeparator",
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={CheckCircleIcon} label={Translate.get("Status")} />,
        display: "flex",
        renderCell: (params) => {
          return (
            <div className="stdFlexLeft" style={{ maxHeight: "0.5em" }}>
              {params.row.dateSpan === "Planerad" && <div className="planerad">{Translate.get("Planned")}</div>}
              {params.row.dateSpan === "Avslutad" && <div className="avslutad">{Translate.get("TimeStatus_Past")}</div>}
              {params.row.dateSpan === "Pågående" && <div className="pagaende">{Translate.get("Ongoing")}</div>}
            </div>
          );
        },
      },
      {
        field: "fromDate",
        headerName: Translate.get("Date"),
        headerClassName: "hideSeparator",
        type: "dateTime",
        valueGetter: (value, row, column, apiRef) => value && new Date(value),
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={CalendarTodayIcon} label={Translate.get("Date")} />,
        renderCell: (params) => {
          return <div>{params.row.fromDate && formatDate(params.row.fromDate)}</div>;
        },
      },
      {
        field: "city",
        headerName: Translate.get("City"),
        headerClassName: "hideSeparator",
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={PlaceIcon} label={Translate.get("City")} />,
        renderCell: (params) => {
          return <div>{params.row.city}</div>;
        },
      },

      {
        field: "responsibleTeacherActorName",
        headerName: Translate.get("Instructor"),
        headerClassName: "hideSeparator",
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={CoPresentIcon} label={Translate.get("Instructor")} />,
        renderCell: (params) => {
          return <div>{params.row.responsibleTeacherActorName}</div>;
        },
      },
      {
        field: "numberOfParticipants",
        headerName: Translate.get("NoParticipants"),
        headerClassName: "hideSeparator",
        flex: 1,
        renderHeader: () => <IconAndLabelColumnHeader icon={GroupIcon} label={Translate.get("NoParticipants")} />,
      },
      ...(hideDotMenu
        ? []
        : [
            {
              field: "dotMenu",
              flex: 0.3,
              headerName: "",
              sortable: false,
              disableColumnMenu: true,
              filterable: false,
              headerClassName: "hideSeparator",
              align: "right",
              hideable: false,
              renderCell: (params) => (
                <div>
                  <DotMenu
                    content={getCourseDotMenu(
                      params.row.courseId,
                      params.row.numberOfBookings,
                      params.row.publishFromDate,
                      params.row.publishToDate,
                      params.row.autoUnpublishWhenFull,
                      params.row.fromDate,
                      onDeleteCourse,
                    )}
                    horizontal
                  />
                </div>
              ),
            },
          ]),
    ];
  }
  //const apiRef = useGridApiRef();
  const handleCellClick = (params, events) => {
    if (!hasAccess(accessKeys.courseAdmin)) {
      return;
    }

    if (params.field !== "dotMenu") {
      setPagination(apiRef, "courses");
      navigate(`/dashboard/course/${params.id}`, { replace: false });
    }
  };
  const apiRef = useGridApiRef();
  const setPagination = (apiDg, page) => {
    const paginationModel = gridPaginationModelSelector(apiDg);
    sessionStorage.setItem("paginationModel_" + page, JSON.stringify(paginationModel));
  };
  const getPagination = (apiDg, page) => {
    //sessionStorage["paginationModel_" + page] ska raderas i  andra komponenter för att nollställa paginering till default
    //Ex: sessionStorage.removeItem("paginationModel_courses");
    if (sessionStorage["paginationModel_" + page]) {
      const paginationModel = gridPaginationModelSelector(apiDg);
      const p = JSON.parse(sessionStorage["paginationModel_" + page]);
      setPaginationModel(p);
      /* setTimeout(() => {
        //Nollstäla om page är för stort, dålig kod
        console.log(apiRef.current.getRowsCount(), p.pageSize, p.page);
        if (apiRef.current.getRowsCount() / p.pageSize < p.page) {
          console.log(
            "Mer än tillgängliga sidor, använd inte sparad paginering"
          );
        } else {
          setPaginationModel(p);
          console.log('Sparad paginering')
        }
      }, 1000); */
    }
  };

  const onFilterChange = useCallback((filterModel) => {
    if (!loadFilters) {
      setFilterModel({ items: [] });
      return;
    }

    filterModel = { filterModel };
    localStorage.setItem("savedFilters", JSON.stringify(filterModel));
  }, []);

  // For small screens
  const listViewColDef = {
    field: "listColumn",
    renderCell: (params) => (
      <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <div style={{ marginTop: "0.5em", minWidth: "300px" }}>
          <TableAvatar2BorderRadius
            image={params.row.imageUrl}
            title={params.row.name}
            subtitle={params.row.shortComment}
            subtitle2={params.row.fromDate ? formatDate(params.row.fromDate) : ""}
          />
        </div>
        <div>
          <DotMenu
            horizontal={true}
            content={getCourseDotMenu(
              params.row.courseId,
              params.row.numberOfBookings,
              params.row.publishFromDate,
              params.row.publishToDate,
              params.row.autoUnpublishWhenFull,
              params.row.fromDate,
              onDeleteCourse,
            )}
          />
        </div>
      </div>
    ),
  };

  return (
    <>
      <DataGridPremium
        showToolbar={!hideFilter}
        key="courseDatagrid"
        rows={rows}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        pageSizeOptions={[15, 50, 100]}
        onPaginationModelChange={setPaginationModel}
        disableRowSelectionOnClick
        getRowId={(row) => row.courseId}
        onCellClick={(params, event) => handleCellClick(params, event)}
        localeText={localizedTextsMap}
        slots={{
          toolbar: hideFilter
            ? null
            : () =>
                ToolBar({
                  hideFilter: smallScreen,
                  showExport: true,
                  exportFileName: Translate.get("Courses"),
                  apiRef: apiRef,
                  wide: true,
                }),
          noRowsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
          noResultsOverlay: CustomNoRowsOverlay(Translate.get("EmptyHere")),
        }}
        rowHeight={isSmallScreen ? 70 : 65}
        disableDensitySelector={true}
        disableColumnMenu={true}
        disableColumnResize={true}
        onFilterModelChange={onFilterChange}
        initialState={{
          pagination: {
            page: 0,
          },
          sorting: { sortModel: [{ field: "fromDate", sort: "desc" }] },
          filter: filterModel,
        }}
        apiRef={apiRef}
        listView={isSmallScreen}
        listViewColumn={isSmallScreen ? listViewColDef : undefined}
      />
    </>
  );
};
export default Comp;
